import { TIER_ORDER } from "../../components/review/tier-labels"
import type { Tier } from "../../scripts/review-ledger"
import type { Blocker } from "../../scripts/lint-review-state"
import type { ReviewerRow, ReviewerScope } from "./reviewer-scope"
import type { BlockedLocale } from "./locale-reachability"

export interface Summary {
  total: number
  byLocale: Record<string, number>
  byTier: Record<string, number>
}

export interface LocaleTierCount {
  tier: Tier
  count: number
}

export interface LocaleStatusRow {
  locale: string
  count: number
  tiers: LocaleTierCount[]
  reviewers: string[]
  unassigned: boolean
  inViewerScope: boolean
}

/**
 * Per-locale fold of the backlog for the PR status board: counts + tier
 * breakdown + the reviewers who can clear each locale, plus whether the viewer
 * can action it themselves. Pure.
 */
export function localeStatus(
  blockers: Blocker[],
  reviewers: ReviewerRow[],
  viewerScope: ReviewerScope
): LocaleStatusRow[] {
  const byLocale = new Map<string, Blocker[]>()
  for (const b of blockers) {
    const list = byLocale.get(b.locale)
    if (list) list.push(b)
    else byLocale.set(b.locale, [b])
  }
  return Array.from(byLocale.entries())
    .map(([locale, items]) => {
      const tierCounts = new Map<Tier, number>()
      for (const b of items)
        tierCounts.set(b.tier, (tierCounts.get(b.tier) ?? 0) + 1)
      const tiers = TIER_ORDER.filter((t) => tierCounts.has(t)).map((tier) => ({
        tier,
        count: tierCounts.get(tier) as number,
      }))
      const presentTiers = Array.from(tierCounts.keys())
      const reviewerSet = new Set<string>()
      let unassigned = false
      for (const tier of presentTiers) {
        const matched = reviewers.filter(
          (r) => r.locales.includes(locale) && r.tiers.includes(tier)
        )
        if (matched.length === 0) unassigned = true
        for (const r of matched) reviewerSet.add(r.email)
      }
      const inViewerScope =
        viewerScope.locales.includes(locale) &&
        presentTiers.some((t) => viewerScope.tiers.includes(t))
      return {
        locale,
        count: items.length,
        tiers,
        reviewers: Array.from(reviewerSet).sort(),
        unassigned,
        inViewerScope,
      }
    })
    .sort((a, b) => a.locale.localeCompare(b.locale))
}

/** Total + per-locale + per-tier counts of review-required strings. */
export function summarize(blockers: Blocker[]): Summary {
  const byLocale: Record<string, number> = {}
  const byTier: Record<string, number> = {}
  for (const b of blockers) {
    byLocale[b.locale] = (byLocale[b.locale] ?? 0) + 1
    byTier[b.tier] = (byTier[b.tier] ?? 0) + 1
  }
  return { total: blockers.length, byLocale, byTier }
}

export interface HelpEntry {
  locale: string
  tier: Tier
  reviewers: string[]
  unassigned: boolean
}

export interface Overview {
  /** Team-wide backlog across every locale + tier. */
  backlog: Summary
  /** Backlog filtered to the viewer's own scope (0 for non-reviewers). */
  scoped: Summary
  whoCanHelp: HelpEntry[]
  /** Reachability-filtered scope — only locales the viewer can currently open. */
  viewerScope: ReviewerScope
  /** Assigned locales gated off behind a preview token, with how to unblock. */
  blocked: BlockedLocale[]
  /** Per-locale board rows for the PR status display. */
  localeStatus: LocaleStatusRow[]
}

/**
 * Aggregate backlog + reviewer matching for the /review overview. Pure.
 *
 * `viewerScope` must already be reachability-filtered (the route intersects the
 * reviewer's assignment with the markets they can open) so `scoped` never
 * counts strings on pages they'd 404 on; `blocked` carries the rest for the
 * unlock hint.
 */
export function buildOverview(
  blockers: Blocker[],
  reviewers: ReviewerRow[],
  viewerScope: ReviewerScope,
  blocked: BlockedLocale[] = []
): Overview {
  const pairs = new Map<string, { locale: string; tier: Tier }>()
  for (const b of blockers)
    pairs.set(`${b.locale}:${b.tier}`, { locale: b.locale, tier: b.tier })
  const whoCanHelp: HelpEntry[] = Array.from(pairs.values())
    .map(({ locale, tier }) => {
      const matched = reviewers
        .filter((r) => r.locales.includes(locale) && r.tiers.includes(tier))
        .map((r) => r.email)
      return {
        locale,
        tier,
        reviewers: matched,
        unassigned: matched.length === 0,
      }
    })
    .sort(
      (a, b) => a.locale.localeCompare(b.locale) || a.tier.localeCompare(b.tier)
    )
  // The viewer's own slice of the backlog — only the locale×tier pairs they can
  // actually action. Empty scope (non-reviewer) → 0, so the header never tells
  // an outsider there are thousands of strings "to review".
  const inScope = blockers.filter(
    (b) =>
      viewerScope.locales.includes(b.locale) &&
      viewerScope.tiers.includes(b.tier)
  )
  return {
    backlog: summarize(blockers),
    scoped: summarize(inScope),
    whoCanHelp,
    viewerScope,
    blocked,
    localeStatus: localeStatus(blockers, reviewers, viewerScope),
  }
}

/** Ready-to-paste Slack/DM nudge for a pending locale's reviewers. Pure. */
export function pingText(row: LocaleStatusRow, reviewLink: string): string {
  const tierBreakdown = row.tiers.map((t) => `${t.tier} ${t.count}`).join(", ")
  const noun = row.count === 1 ? "string" : "strings"
  return [
    `Translation review needed — ${row.locale} (${row.count} ${noun}: ${tierBreakdown}) on this PR:`,
    reviewLink,
    `Reviewers: ${row.reviewers.join(", ")}`,
  ].join("\n")
}

/** Pull the first marketing-site preview URL out of a deploy bot comment body. */
export function extractPreviewUrl(commentBody: string): string | null {
  const m = commentBody.match(
    /https:\/\/[a-z0-9-]+\.vercel\.app(?:\/[^\s)]*)?/i
  )
  return m ? m[0].replace(/\/$/, "") : null
}
